import { type INTEGRATIONS, type INTEGRATIONS_V2 } from '../../components/IntegrationButton';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectCompany } from 'src/old/actions/company/companySelectors';
import { useHistory } from 'react-router';
import { useGetCompaniesPaginateAllQuery } from 'src/shared/api/company';
import { type HandleSubmit, NOT_SELECTED_INTEGRATION_NAME } from '../../hooks';
import { type Company as CurrentCompany } from 'src/old/api/types';
import { type Company } from 'src/shared/types/company';
import { hasIntegrationForm } from './forms';

export type IntegrationProps = {
  currentCompany: CurrentCompany | null;
  companyId: string | null;
  subsidiaries: Company[] | null;
  handleSubmit: HandleSubmit;
  integrationName?: string;
};

type useIntegrationProps = {
  handleSubmit: HandleSubmit;
  integrationName: INTEGRATIONS | INTEGRATIONS_V2 | 'none';
};

export const useIntegration = ({ handleSubmit, integrationName }: useIntegrationProps): IntegrationProps => {
  const history = useHistory();
  const { data: companies } = useGetCompaniesPaginateAllQuery({ searchValue: '' });

  const [integrationCredentials, setIntegrationCredentials] = useState({
    clientId: '',
    secret: ''
  });
  const [integrationSettings, setIntegrationSettings] = useState({
    teams: []
  });

  const currentCompany = useSelector(selectCompany);

  // Left it here to support old version of integrations
  useEffect(() => {
    setIntegrationSettings(currentCompany?.integrationSettings);
    setIntegrationCredentials(currentCompany?.integrationCredentials);
  }, [currentCompany]);

  const goToIntegrationsPage = useCallback(() => {
    history.push(`/settings/integrations`);
  }, [history]);

  useEffect(() => {
    if ((integrationName as string) === NOT_SELECTED_INTEGRATION_NAME) {
      goToIntegrationsPage();
      return;
    }
    const locationPath = history.location.pathname.split('/');
    const maybeIntegrationName = locationPath[locationPath.length - 1];

    if (!hasIntegrationForm(maybeIntegrationName)) {
      goToIntegrationsPage();
      return;
    }

    if (maybeIntegrationName === integrationName) {
      return;
    }
    history.push(`/settings/integrations/${integrationName}`);
  }, [goToIntegrationsPage, history, integrationName]);

  const props = {
    ...integrationCredentials,
    ...integrationSettings,
    currentCompany,
    companyId: currentCompany?.id,
    subsidiaries: companies,
    handleSubmit
  };

  const typedProps: IntegrationProps = props as IntegrationProps;

  return typedProps;
};
